import React, { useState, useEffect } from 'react';
import { TrendingUp, Eye, Heart, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';

function BarChart({ data, color }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={styles.chart}>
      <div style={styles.chartBars}>
        {data.map((d, i) => (
          <div key={i} style={styles.barWrapper}>
            <div
              style={{
                ...styles.bar,
                height: `${(d.value / max) * 100}%`,
                background: `linear-gradient(to top, ${color}, ${color}88)`,
              }}
              title={`${d.label}: ${d.value.toLocaleString()}`}
            />
            <span style={styles.barLabel}>{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, color }) {
  return (
    <div style={styles.statBox}>
      <div style={{ ...styles.statIcon, background: color + '22', color }}>
        <Icon size={18} />
      </div>
      <p style={styles.statBoxLabel}>{label}</p>
      <p style={styles.statBoxValue}>{value}</p>
    </div>
  );
}

export default function Analytics() {
  const [period, setPeriod] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const periods = ['7d', '30d', '90d'];

  useEffect(() => {
    setLoading(true);
    const days = period === '90d' ? 90 : period === '30d' ? 30 : 7;
    const since = new Date(Date.now() - days * 86400_000).toISOString();

    const load = async () => {
      const { data: rows } = await supabase.from('videos')
        .select('id, title, views, likes, published_at, tiktok_accounts(username, avatar_url)')
        .eq('status', 'published')
        .gte('published_at', since)
        .order('views', { ascending: false });

      const videos = (rows ?? []).map(({ tiktok_accounts, ...v }) => ({
        ...v, account_name: tiktok_accounts?.username ?? null,
      }));

      const dailyMap = {};
      for (const v of videos) {
        const day = v.published_at?.slice(0, 10);
        if (!day) continue;
        if (!dailyMap[day]) dailyMap[day] = { views: 0, likes: 0 };
        dailyMap[day].views += v.views ?? 0;
        dailyMap[day].likes += v.likes ?? 0;
      }

      setData({
        overview: {
          total_views: videos.reduce((s, v) => s + (v.views ?? 0), 0),
          total_likes: videos.reduce((s, v) => s + (v.likes ?? 0), 0),
          total_videos: videos.length,
        },
        daily: Object.entries(dailyMap).sort().map(([date, d]) => ({ date, ...d })),
        top_videos: videos.slice(0, 10),
        by_account: [],
      });
      setLoading(false);
    };
    load().catch(() => setLoading(false));
  }, [period]);

  const formatNum = n => {
    if (!n) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
  };

  const viewsChartData = (data?.daily || []).map(d => ({
    label: d.date ? d.date.slice(5) : '',
    value: Number(d.views) || 0,
  }));
  const likesChartData = (data?.daily || []).map(d => ({
    label: d.date ? d.date.slice(5) : '',
    value: Number(d.likes) || 0,
  }));

  const totalViews = Number(data?.overview?.total_views) || 0;
  const totalLikes = Number(data?.overview?.total_likes) || 0;
  const totalVideos = Number(data?.overview?.total_videos) || 0;
  const engagementRate = totalViews > 0 ? ((totalLikes / totalViews) * 100).toFixed(1) : '0.0';

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Analytics</h1>
          <p style={styles.subtitle}>Track your content performance</p>
        </div>
        <div style={styles.periodToggle}>
          {periods.map(p => (
            <button
              key={p}
              style={{
                ...styles.periodBtn,
                background: period === p ? 'var(--accent)' : 'transparent',
                color: period === p ? '#fff' : 'var(--text-secondary)',
              }}
              onClick={() => setPeriod(p)}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 14, textAlign: 'center', padding: '40px 0' }}>Loading...</p>
      ) : (
        <>
          <div style={styles.statsGrid}>
            <StatBox icon={Eye} label="Total Views" value={formatNum(totalViews)} color="var(--cyan)" />
            <StatBox icon={Heart} label="Total Likes" value={formatNum(totalLikes)} color="var(--pink)" />
            <StatBox icon={Users} label="Videos Published" value={String(totalVideos)} color="var(--green)" />
            <StatBox icon={TrendingUp} label="Engagement Rate" value={`${engagementRate}%`} color="var(--yellow)" />
          </div>

          <div style={styles.chartsRow}>
            <div style={styles.chartCard}>
              <div style={styles.chartHeader}>
                <h3 style={styles.chartTitle}>Views</h3>
                <span style={styles.chartTotal}>{formatNum(totalViews)} total</span>
              </div>
              {viewsChartData.length > 0
                ? <BarChart data={viewsChartData} color="var(--cyan)" />
                : <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '40px 0' }}>No data yet</p>
              }
            </div>
            <div style={styles.chartCard}>
              <div style={styles.chartHeader}>
                <h3 style={styles.chartTitle}>Likes</h3>
                <span style={styles.chartTotal}>{formatNum(totalLikes)} total</span>
              </div>
              {likesChartData.length > 0
                ? <BarChart data={likesChartData} color="var(--pink)" />
                : <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '40px 0' }}>No data yet</p>
              }
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Top Performing Videos</h3>
            </div>
            {(data?.top_videos || []).length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No published videos yet</p>
            ) : (
              <div style={styles.videoTable}>
                <div style={styles.tableHeader}>
                  <span style={{ flex: 3 }}>Video</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>Views</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>Likes</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>Engagement</span>
                </div>
                {(data?.top_videos || []).map((v, i) => (
                  <div key={v.id} style={styles.tableRow}>
                    <div style={{ flex: 3, display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <span style={styles.rankNum}>{i + 1}</span>
                      <div style={styles.thumbSmall}>📹</div>
                      <div style={{ minWidth: 0 }}>
                        <p style={styles.videoName}>{v.title}</p>
                        <p style={styles.videoAcct}>{v.account_name ? `@${v.account_name}` : '—'}</p>
                      </div>
                    </div>
                    <span style={{ flex: 1, textAlign: 'right', fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>
                      {formatNum(v.views)}
                    </span>
                    <span style={{ flex: 1, textAlign: 'right', fontSize: 13, color: 'var(--text-secondary)' }}>
                      {formatNum(v.likes)}
                    </span>
                    <span style={{ flex: 1, textAlign: 'right', fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>
                      {v.views > 0 ? `${((v.likes / v.views) * 100).toFixed(1)}%` : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Account Performance</h3>
            </div>
            {(data?.by_account || []).length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No connected accounts</p>
            ) : (
              <div style={styles.accountList}>
                {(data?.by_account || []).map(acc => {
                  const maxViews = Math.max(...(data?.by_account || []).map(a => Number(a.total_views) || 0), 1);
                  const pct = Math.round((Number(acc.total_views) / maxViews) * 100) || 0;
                  return (
                    <div key={acc.id} style={styles.accountRow}>
                      <div style={styles.accAvatar}>
                        {acc.avatar_url
                          ? <img src={acc.avatar_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                          : (acc.username || 'T')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <p style={styles.accName}>@{acc.username}</p>
                          <p style={styles.accPct}>{pct}%</p>
                        </div>
                        <div style={styles.progressBg}>
                          <div style={{ ...styles.progressBar, width: `${pct}%` }} />
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', marginLeft: 16 }}>
                        <p style={styles.accFollowers}>{formatNum(acc.followers)}</p>
                        <p style={styles.accFollowersLabel}>followers</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  container: { flex: 1, padding: '32px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 1100 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  title: { fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 },
  subtitle: { fontSize: 14, color: 'var(--text-secondary)' },
  periodToggle: { display: 'flex', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 4, gap: 4 },
  periodBtn: { padding: '7px 16px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.2s' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 },
  statBox: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '18px', display: 'flex', flexDirection: 'column', gap: 8 },
  statIcon: { width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  statBoxLabel: { fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 },
  statBoxValue: { fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' },
  chartsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
  chartCard: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px' },
  chartHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  chartTitle: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' },
  chartTotal: { fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 },
  chart: { height: 140 },
  chartBars: { display: 'flex', alignItems: 'flex-end', height: '100%', gap: 6 },
  barWrapper: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', gap: 6 },
  bar: { width: '100%', borderRadius: '4px 4px 0 0', flex: 1, alignSelf: 'flex-end', transition: 'height 0.3s ease', minHeight: 4, cursor: 'default', maxHeight: '100%' },
  barLabel: { fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 },
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px' },
  cardHeader: { marginBottom: 16 },
  cardTitle: { fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' },
  videoTable: { display: 'flex', flexDirection: 'column', gap: 2 },
  tableHeader: { display: 'flex', padding: '6px 12px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 4 },
  tableRow: { display: 'flex', alignItems: 'center', padding: '10px 12px', borderRadius: 10, background: 'var(--bg-secondary)', gap: 12 },
  rankNum: { fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', width: 18, textAlign: 'center' },
  thumbSmall: { fontSize: 18, flexShrink: 0 },
  videoName: { fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  videoAcct: { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 },
  accountList: { display: 'flex', flexDirection: 'column', gap: 14 },
  accountRow: { display: 'flex', alignItems: 'center', gap: 12 },
  accAvatar: { width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent), var(--pink))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0, overflow: 'hidden' },
  accName: { fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' },
  accPct: { fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' },
  progressBg: { height: 6, background: 'var(--bg-secondary)', borderRadius: 3, overflow: 'hidden' },
  progressBar: { height: '100%', background: 'linear-gradient(90deg, var(--accent), var(--pink))', borderRadius: 3, transition: 'width 0.5s ease' },
  accFollowers: { fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' },
  accFollowersLabel: { fontSize: 10, color: 'var(--text-muted)', textAlign: 'right' },
};
